import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from '@/components/icons';

/**
 * `<DeprecationBanner />` (#1517 / W52-F2-S2) — in-app legacy-platform sunset
 * notice.
 *
 * Always-visible strip warning users that the legacy starfish-app platform is
 * being retired, with a link to migration info / the sunset plan. Mounted in
 * the app shell so every authenticated user sees the notice the moment it is
 * activated.
 *
 * **Config-gated, default-OFF.** The banner only renders when
 * `VITE_SUNSET_BANNER_ENABLED === 'true'`. An optional `VITE_SUNSET_DATE`
 * (ISO date) is interpolated into the copy, and an optional
 * `VITE_SUNSET_MIGRATION_URL` overrides the migration link target. Shipping
 * default-OFF means the component is inert until ops flips the flag at
 * announcement time — no surprise banner on merge.
 *
 * **Dismissible + persistent.** Dismissal is stored in `localStorage` (keyed
 * by the active sunset date so a *new* announcement re-surfaces) so it doesn't
 * nag on every load. The close control is a real `<button>` — keyboard
 * dismissal works for free.
 *
 * **Accessible.** Wrapped in a `role="region"` landmark with an
 * `aria-label`, and carries `role="alert"` so assistive tech announces the
 * sunset notice when it first appears.
 */

const STORAGE_KEY_PREFIX = 'senviro.deprecation-banner.dismissed';
const DEFAULT_MIGRATION_URL = 'https://docs.sustevo.com/migration';

interface SunsetConfig {
  enabled: boolean;
  /** ISO date string (YYYY-MM-DD) the legacy platform is retired, if set. */
  sunsetDate: string | null;
  migrationUrl: string;
}

/**
 * Reads the sunset config from Vite env. Exported for unit-test reach.
 * `import.meta.env.*` is statically inlined by Vite, so the values are
 * baked at build time — flipping the banner on is a redeploy, by design.
 */
export function readSunsetConfig(): SunsetConfig {
  const enabled = import.meta.env.VITE_SUNSET_BANNER_ENABLED === 'true';
  const rawDate = import.meta.env.VITE_SUNSET_DATE;
  const sunsetDate =
    typeof rawDate === 'string' && rawDate.trim().length > 0
      ? rawDate.trim()
      : null;
  const rawUrl = import.meta.env.VITE_SUNSET_MIGRATION_URL;
  const migrationUrl =
    typeof rawUrl === 'string' && rawUrl.trim().length > 0
      ? rawUrl.trim()
      : DEFAULT_MIGRATION_URL;
  return { enabled, sunsetDate, migrationUrl };
}

/**
 * Dismissal is keyed by the active sunset date so re-announcing with a new
 * date (or first announcing without one) re-surfaces the banner for users who
 * had dismissed a prior run.
 */
function storageKey(sunsetDate: string | null): string {
  return `${STORAGE_KEY_PREFIX}:${sunsetDate ?? 'unscheduled'}`;
}

function readDismissed(key: string): boolean {
  try {
    return typeof window !== 'undefined'
      ? window.localStorage.getItem(key) === 'true'
      : false;
  } catch {
    // localStorage unavailable (private mode / blocked); treat as not dismissed.
    return false;
  }
}

function writeDismissed(key: string): void {
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, 'true');
    }
  } catch {
    // Silent — a failed persist just means the banner re-shows next load.
  }
}

export function DeprecationBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const config = readSunsetConfig();
  const key = storageKey(config.sunsetDate);
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(key));

  if (!config.enabled || dismissed) return null;

  const message = config.sunsetDate
    ? t('deprecationBanner.messageDated', { date: config.sunsetDate })
    : t('deprecationBanner.message');

  const handleDismiss = (): void => {
    writeDismissed(key);
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label={t('deprecationBanner.regionLabel')}
      data-testid="deprecation-banner"
      className="flex items-center justify-center gap-2 border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm font-medium text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span role="alert" className="text-center">
        {message}{' '}
        <a
          href={config.migrationUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="deprecation-banner-link"
          className="font-semibold underline underline-offset-2 hover:no-underline"
        >
          {t('deprecationBanner.link')}
        </a>
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('deprecationBanner.dismiss')}
        data-testid="deprecation-banner-dismiss"
        className="ml-1 shrink-0 rounded-md p-1 text-amber-900 transition hover:bg-amber-200 dark:text-amber-100 dark:hover:bg-amber-900"
      >
        <X size={14} weight="regular" aria-hidden="true" />
      </button>
    </div>
  );
}
